/* eslint-disable class-methods-use-this */
import PublicBaseEventRepository from "@app-contexts/public/features/event-detail/data/repositories/PublicBaseEventRepository";

import {
  AuthorizationType,
  type AuthorizationStrategy,
  type AuthorizationResult,
} from "../types";
import { TicketTypePasswordStrategy } from "./TicketTypePasswordStrategy";

export class EventPasswordStrategy implements AuthorizationStrategy {
  type = AuthorizationType.EVENT_PASSWORD;

  /**
   * Builds authorization keys for event password authorization.
   * Only handles event-level authorization keys.
   * @param eventId - The event ID
   * @returns Array of event authorization keys
   */
  static buildAuthorizationKeys(eventId: string): string[] {
    return [`${AuthorizationType.EVENT_PASSWORD}:${eventId}`];
  }

  /**
   * Workaround
   * When an event is authorized but no specific ticket types are returned (empty array),
   * it means ALL ticket types of the event should be authorized.
   * This is a backend limitation - it should explicitly tell us which ticket types
   * are authorized, but instead we need to infer it from an empty response.
   */
  private async resolveTicketTypeIds(
    ticketTypeIds: number[],
    eventId: string,
    repo: PublicBaseEventRepository
  ): Promise<number[]> {
    if (!ticketTypeIds || ticketTypeIds.length === 0) {
      try {
        const eventResponse = await repo.retrieve(eventId);
        // Recurring parents' ticket type IDs differ from their children's.
        // Resolving to parent IDs would cause displayTicketType to filter out
        // every child ticket type. Return [] so downstream treats it as
        // "all authorized."
        if (eventResponse.is_recurring_parent) return [];
        return eventResponse.ticket_types?.map((tt) => tt.id) || [];
      } catch (error) {
        return [];
      }
    }
    return ticketTypeIds;
  }

  async check(
    eventSlug: string,
    isAuthorizedFn: (type: AuthorizationType, resourceId: string) => boolean,
    metadata: Record<string, string>
  ): Promise<AuthorizationResult> {
    // First check if we already have authorization in memory/storage
    if (isAuthorizedFn(AuthorizationType.EVENT_PASSWORD, eventSlug)) {
      // Already authorized, return empty result (no new authorizations to add)
      return { authorized: true, authorizations: [] };
    }

    // If not authorized in memory, check with backend
    try {
      const repo = new PublicBaseEventRepository();
      const response = await repo.checkEventPasswordAuthorized(eventSlug, {
        showToastOnError: false,
      });

      // Resolve ticket type IDs using shared logic
      const ticketTypeIds = await this.resolveTicketTypeIds(
        response.tickettypes || [],
        eventSlug,
        repo
      );

      // Build authorization keys for event and ticket types
      // Backend workaround: We add both slug and ID for cross-compatibility reasons. The backend uses slugs for events, ids for ticket types.
      // If we wanted to check for event authrzn based on tt data, all we have is an ID.
      // Ideally only IDs are used across the stack.
      const eventKeysBySlug =
        EventPasswordStrategy.buildAuthorizationKeys(eventSlug);
      const eventKeysById = EventPasswordStrategy.buildAuthorizationKeys(
        metadata.eventId
      );
      const ticketTypeKeys = ticketTypeIds.flatMap((ttId) =>
        TicketTypePasswordStrategy.buildAuthorizationKeys(ttId.toString())
      );
      const allKeys = [...eventKeysBySlug, ...eventKeysById, ...ticketTypeKeys];

      return {
        authorized: true,
        authorizations: allKeys,
        metadata: {
          password_session_id: response.password_session_id,
        },
      };
    } catch (error) {
      // Not authorized, return empty result
      return { authorized: false, authorizations: [] };
    }
  }

  async authorize(
    eventSlug: string,
    credentials: unknown,
    metadata: Record<string, string>
  ): Promise<AuthorizationResult> {
    const password = credentials as string;
    const repo = new PublicBaseEventRepository();
    const response = await repo.checkEventPassword({
      password,
      slug: eventSlug,
    });

    // Resolve ticket type IDs using shared logic
    const ticketTypeIds = await this.resolveTicketTypeIds(
      response.tickettypes || [],
      eventSlug,
      repo
    );

    // Build authorization keys for event and ticket types
    // Backend workaround: We add both slug and ID for cross-compatibility reasons. The backend uses slugs for events, ids for ticket types.
    // Ideally only IDs are used across the stack.
    const eventKeysBySlug =
      EventPasswordStrategy.buildAuthorizationKeys(eventSlug);
    const eventKeysById = EventPasswordStrategy.buildAuthorizationKeys(
      metadata.eventId
    );
    const ticketTypeKeys = ticketTypeIds.flatMap((ttId) =>
      TicketTypePasswordStrategy.buildAuthorizationKeys(ttId.toString())
    );
    const allKeys = [...eventKeysBySlug, ...eventKeysById, ...ticketTypeKeys];

    return {
      authorized: true,
      authorizations: allKeys,
      metadata: {
        password_session_id: response.password_session_id,
      },
    };
  }

  async revoke(eventId: string): Promise<string[]> {
    // For event passwords, we'd need to determine which ticket types were authorized by this event
    // For now, just return the event key - more complex logic could be added later
    return [`${AuthorizationType.EVENT_PASSWORD}:${eventId}`];
  }
}
